import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, FileText, CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

// GDPR Article 25: Privacy by design - sensitive data never stored in profile
// ROA 2014: Rehabilitation of Offenders Act compliance for disclosure
interface ResumeData {
  experiences: string[];
  skills: string[];
  professionalSummary: string;
  cvSummaries?: string[];
  // Criminal history for ROA assessment (ephemeral - not persisted)
  convictionDate?: string;
  convictionType?: string;
  sentenceLength?: string;
}

export default function ResumeBuilder() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Form state
  const [experienceInput, setExperienceInput] = useState("");
  const [resumeData, setResumeData] = useState<ResumeData>({
    experiences: [],
    skills: [],
    professionalSummary: "",
    cvSummaries: [],
  });
  
  // CV Summaries input state
  const [targetRole, setTargetRole] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [keySkills, setKeySkills] = useState("");
  const [majorAchievements, setMajorAchievements] = useState("");
  
  // ROA disclosure state (ephemeral - never saved to database)
  const [convictionDate, setConvictionDate] = useState("");
  const [convictionType, setConvictionType] = useState("");
  const [sentenceLength, setSentenceLength] = useState("");
  const [disclosureGuidance, setDisclosureGuidance] = useState<any>(null);

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  // AI-powered experience transformation
  const handleExperienceTransform = async () => {
    if (!experienceInput.trim()) {
      toast({
        title: "Input Required",
        description: "Please describe your experience first.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "experience",
          userData: { rawExperience: experienceInput }
        }
      });

      if (error) throw error;

      // GDPR Article 5(1)(c): Data minimisation - only store transformed professional skills
      const transformedExperiences = data.result;
      setResumeData(prev => ({
        ...prev,
        experiences: [...prev.experiences, ...(Array.isArray(transformedExperiences) ? transformedExperiences : [transformedExperiences])]
      }));

      toast({
        title: "Experience Transformed ✨",
        description: "Your experience has been professionally reframed.",
      });

      setExperienceInput("");
    } catch (error: any) {
      console.error("Transform error:", error);
      toast({
        title: "AI Processing Error",
        description: error.message || "Failed to transform experience. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // AI-powered skills extraction
  const handleSkillsGeneration = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "skills",
          userData: { experiences: resumeData.experiences }
        }
      });

      if (error) throw error;

      const extractedSkills = data.result;
      setResumeData(prev => ({
        ...prev,
        skills: Array.isArray(extractedSkills) ? extractedSkills : []
      }));

      toast({
        title: "Skills Identified ✨",
        description: "AI has identified your transferable skills.",
      });

      setStep(3);
    } catch (error: any) {
      console.error("Skills error:", error);
      toast({
        title: "AI Processing Error",
        description: error.message || "Failed to generate skills.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // ROA compliance assessment
  const handleDisclosureAssessment = async () => {
    if (!convictionDate || !convictionType || !sentenceLength) {
      toast({
        title: "Information Required",
        description: "Please provide all conviction details for ROA assessment.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Security: Criminal history sent to AI but NEVER stored in database
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "disclosure",
          userData: {
            convictionDate,
            convictionType,
            sentenceLength
          }
        }
      });

      if (error) throw error;

      setDisclosureGuidance(data.result);
      
      toast({
        title: "ROA Assessment Complete",
        description: "Disclosure guidance generated based on UK law.",
      });

      setStep(4);
    } catch (error: any) {
      console.error("Disclosure error:", error);
      toast({
        title: "Assessment Error",
        description: error.message || "Failed to assess disclosure requirements.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Professional summary generation
  const handleSummaryGeneration = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "summary",
          userData: {
            experiences: resumeData.experiences,
            skills: resumeData.skills
          }
        }
      });

      if (error) throw error;

      setResumeData(prev => ({
        ...prev,
        professionalSummary: data.result.summary || ""
      }));

      toast({
        title: "Resume Complete! ✨",
        description: "Your professional resume has been generated.",
      });

    } catch (error: any) {
      console.error("Summary error:", error);
      toast({
        title: "Generation Error",
        description: error.message || "Failed to generate summary.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // CV Summaries generation
  const handleCVSummariesGeneration = async () => {
    if (!targetRole || !yearsExperience || !keySkills || !majorAchievements) {
      toast({
        title: "Input Required",
        description: "Please fill in all fields to generate CV summaries.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "cv-summaries",
          userData: {
            target_role: targetRole,
            years_experience: yearsExperience,
            key_skills: keySkills,
            major_achievements: majorAchievements
          }
        }
      });

      if (error) throw error;

      const summaries = data.result.summaries || [];
      setResumeData(prev => ({
        ...prev,
        cvSummaries: summaries
      }));

      toast({
        title: "CV Summaries Generated ✨",
        description: "3 professional CV summaries have been created.",
      });

    } catch (error: any) {
      console.error("CV summaries error:", error);
      toast({
        title: "Generation Error",
        description: error.message || "Failed to generate CV summaries.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">AI Resume Builder</h3>
          <span className="text-sm text-muted-foreground">Step {step} of {totalSteps}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step 1: Experience Input */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Transform Your Experience
            </CardTitle>
            <CardDescription>
              Describe any experience - work, volunteering, education, or life skills. 
              AI will help reframe it professionally.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="experience">Your Experience</Label>
              <Textarea
                id="experience"
                placeholder="Example: I volunteered at a community garden, organized weekly tasks for 10 volunteers, and managed supply inventory..."
                value={experienceInput}
                onChange={(e) => setExperienceInput(e.target.value)}
                rows={6}
              />
            </div>

            {resumeData.experiences.length > 0 && (
              <div className="space-y-2">
                <Label>Transformed Achievements</Label>
                <ul className="space-y-1">
                  {resumeData.experiences.map((exp, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      {exp}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleExperienceTransform}
                disabled={isProcessing || !experienceInput.trim()}
                className="flex-1"
              >
                {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Sparkles className="h-4 w-4 mr-2" />
                Transform with AI
              </Button>
              
              {resumeData.experiences.length > 0 && (
                <Button
                  onClick={handleSkillsGeneration}
                  disabled={isProcessing}
                  variant="default"
                >
                  Next: Extract Skills
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Skills Extraction */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Your Transferable Skills
            </CardTitle>
            <CardDescription>
              AI has identified these professional skills from your experiences.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {resumeData.skills.map((skill, idx) => (
                <div key={idx} className="px-3 py-2 bg-primary/10 rounded-md text-sm">
                  {skill}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1">
                Next: Disclosure Guidance
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: ROA Disclosure Assessment */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Criminal History Disclosure (Optional)</CardTitle>
            <CardDescription>
              Get ROA-compliant guidance. This information is NEVER stored - used only for real-time assessment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="conviction-date">Conviction Date</Label>
              <Input
                id="conviction-date"
                type="date"
                value={convictionDate}
                onChange={(e) => setConvictionDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="conviction-type">Conviction Type</Label>
              <Select value={convictionType} onValueChange={setConvictionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary Offence</SelectItem>
                  <SelectItem value="either-way">Either-Way Offence</SelectItem>
                  <SelectItem value="indictable">Indictable Offence</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sentence">Sentence Length</Label>
              <Select value={sentenceLength} onValueChange={setSentenceLength}>
                <SelectTrigger>
                  <SelectValue placeholder="Select length" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fine-only">Fine Only</SelectItem>
                  <SelectItem value="under-6-months">Under 6 Months</SelectItem>
                  <SelectItem value="6-30-months">6-30 Months</SelectItem>
                  <SelectItem value="over-30-months">Over 30 Months</SelectItem>
                  <SelectItem value="over-4-years">Over 4 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={handleDisclosureAssessment} disabled={isProcessing} className="flex-1">
                {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Get ROA Guidance
              </Button>
              <Button onClick={() => setStep(4)} variant="secondary" className="flex-1">
                Skip
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Final Summary & Disclosure Guidance */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Your Professional Resume
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!resumeData.professionalSummary ? (
              <Button onClick={handleSummaryGeneration} disabled={isProcessing} className="w-full">
                {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Professional Summary
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Professional Summary</Label>
                  <p className="text-sm">{resumeData.professionalSummary}</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Key Achievements</Label>
                  <ul className="space-y-1">
                    {resumeData.experiences.map((exp, idx) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {exp}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Core Skills</Label>
                  <div className="flex flex-wrap gap-2">
                    {resumeData.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary/10 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CV Summaries Section */}
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
                  <Label className="text-base font-semibold">Generate CV Summaries</Label>
                  <p className="text-sm text-muted-foreground">
                    Get 3 professional CV summary variations tailored to your target role.
                  </p>
                  
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="target-role">Target Role</Label>
                      <Input
                        id="target-role"
                        placeholder="e.g., Senior Software Engineer"
                        value={targetRole}
                        onChange={(e) => setTargetRole(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="years-exp">Years of Experience</Label>
                      <Input
                        id="years-exp"
                        placeholder="e.g., 8"
                        value={yearsExperience}
                        onChange={(e) => setYearsExperience(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="key-skills">Key Skills (comma-separated)</Label>
                      <Textarea
                        id="key-skills"
                        placeholder="e.g., JavaScript, React, Node.js, Database Design"
                        value={keySkills}
                        onChange={(e) => setKeySkills(e.target.value)}
                        rows={2}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="achievements">Major Achievements</Label>
                      <Textarea
                        id="achievements"
                        placeholder="e.g., Increased app performance by 40%, Led team of 5 developers"
                        value={majorAchievements}
                        onChange={(e) => setMajorAchievements(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <Button 
                      onClick={handleCVSummariesGeneration} 
                      disabled={isProcessing}
                      className="w-full"
                    >
                      {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate 3 CV Summaries
                    </Button>
                  </div>

                  {resumeData.cvSummaries && resumeData.cvSummaries.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <Label className="text-base font-semibold">Your CV Summary Options:</Label>
                      {resumeData.cvSummaries.map((summary, idx) => (
                        <div key={idx} className="p-3 bg-background rounded-md border">
                          <div className="text-xs text-muted-foreground mb-1">Option {idx + 1}</div>
                          <p className="text-sm">{summary}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {disclosureGuidance && (
                  <div className="space-y-2 p-4 bg-muted rounded-lg">
                    <Label className="text-base font-semibold">ROA Disclosure Guidance</Label>
                    <div className="space-y-2 text-sm">
                      <p>
                        <strong>Status:</strong> {disclosureGuidance.isSpent ? "Spent" : "Unspent"}
                      </p>
                      <p>
                        <strong>Recommended Disclosure Level:</strong>{" "}
                        {disclosureGuidance.disclosureLevel === 0 && "No disclosure required"}
                        {disclosureGuidance.disclosureLevel === 1 && "Basic DBS only"}
                        {disclosureGuidance.disclosureLevel === 2 && "Standard DBS"}
                        {disclosureGuidance.disclosureLevel === 3 && "Enhanced DBS"}
                      </p>
                      <p className="text-muted-foreground">{disclosureGuidance.guidance}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                    Start Over
                  </Button>
                  <Button className="flex-1">
                    Download Resume
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
